import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import VotePackageCard from "../components/VotePackages/VotePackageCard";
import VotePurchaseModal from "../components/VotePackages/VotePurchaseModal";
import { votePurchaseService } from "../services/votePurchaseService";
import VotingPageShell from "../components/voting/VotingPageShell";

function BuyVotes() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isRecoveringPayment, setIsRecoveringPayment] = useState(false);
  const [recoverPollCount, setRecoverPollCount] = useState(0);
  const [stats, setStats] = useState({
    totalVotes: 0,
    recentPurchases: [],
  });

  useEffect(() => {
    fetchPackages();
    fetchUserStats();
    resumePendingVoteVerification();
  }, []);

  const hasPendingPayment = () => {
    return Boolean(votePurchaseService.getPendingVotePayment()?.referenceId);
  };

  const resumePendingVoteVerification = async (count = 0) => {
    const pending = votePurchaseService.getPendingVotePayment();
    if (!pending?.referenceId) {
      return;
    }

    setIsRecoveringPayment(true);
    setRecoverPollCount(count);

    if (count > 60) {
      setIsRecoveringPayment(false);
      toast.info("Payment is still pending. You can verify again shortly.");
      return;
    }

    try {
      const verification = await votePurchaseService.verifyPendingVotePayment();

      if (verification.status === "successful") {
        toast.success("Pending vote payment verified. Votes added successfully.");
        setIsRecoveringPayment(false);
        setRecoverPollCount(0);
        fetchUserStats();
        fetchPackages();
        return;
      }

      if (verification.status === "failed") {
        const errorMessage = verification.message || "Pending vote payment failed or was cancelled.";
        toast.error(errorMessage);
        setIsRecoveringPayment(false);
        setRecoverPollCount(0);
        return;
      }

      setTimeout(() => resumePendingVoteVerification(count + 1), 5000);
    } catch (error) {
      console.error("Error during pending payment verification:", error);
      setTimeout(() => resumePendingVoteVerification(count + 1), 5000);
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const data = await votePurchaseService.getVotePackages();
      setPackages(data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load vote packages");
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const balance = await votePurchaseService.getUserVoteBalance();
      const history = await votePurchaseService.getPurchaseHistory(5);

      setStats({
        totalVotes: balance?.totalVotesRemaining,
        recentPurchases: history?.data || [],
      });
    } catch (error) {
      console.error("Error fetching user stats:", error);
      // Don't show error toast for stats
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowModal(true);
  };

  const handlePurchaseSuccess = () => {
    fetchUserStats();
    fetchPackages();
  };

  const separatePackages = () => {
    const daily = packages.filter((p) => p.type === "daily");
    const weekly = packages.filter((p) => p.type === "weekly");
    return { daily, weekly };
  };

  const { daily, weekly } = separatePackages();

  return (
    <VotingPageShell className="px-5 py-6 sm:px-8 lg:px-10">
      <div className="mx-auto max-w-7xl pb-12 pt-4 md:pt-8">
          <header className="mb-8 flex flex-col gap-4 border-b border-line pb-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="eyebrow-accent mb-2">Voting · Vote packages</p>
              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Add vote credits</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400 sm:text-base">
                Choose a package to support your favorites in active campaigns.
              </p>
            </div>
            <div className="rounded-xl border border-accent-dim bg-accent/10 px-5 py-3">
              <p className="eyebrow">Available votes</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-white">{stats.totalVotes?.toLocaleString() || "0"}</p>
            </div>
          </header>

          {/* Package availability */}
          <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Daily Packages Available */}
            <div className="rounded-xl border border-line bg-surface-850 p-5">
              <p className="eyebrow mb-2">Daily packages</p>
              <p className="text-3xl font-bold tracking-tight text-white">{daily.length}</p>
              <p className="mt-2 text-xs text-gray-500">available options</p>
            </div>

            {/* Weekly Packages Available */}
            <div className="rounded-xl border border-line bg-surface-850 p-5">
              <p className="eyebrow mb-2">Weekly packages</p>
              <p className="text-3xl font-bold tracking-tight text-white">{weekly.length}</p>
              <p className="mt-2 text-xs text-gray-500">available options</p>
            </div>
          </div>

          {hasPendingPayment() && (
            <div className="mb-8 flex flex-col items-start justify-between gap-3 rounded-xl border border-amber-800/50 bg-amber-950/20 p-4 md:flex-row md:items-center">
              <div>
                <p className="text-white font-semibold">
                  Pending mobile payment detected
                </p>
                <p className="text-gray-400 text-sm">
                  {isRecoveringPayment
                    ? `Verifying payment status... poll ${recoverPollCount}/60`
                    : "If you already approved payment on your phone, verify now to finalize vote crediting."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => resumePendingVoteVerification(0)}
                disabled={isRecoveringPayment}
                className="btn-secondary"
              >
                {isRecoveringPayment ? "Verifying..." : "Verify Pending Payment"}
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center min-h-96">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-surface-600 border-t-accent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-400">Loading vote packages...</p>
              </div>
            </div>
          ) : packages.length === 0 ? (
            <div className="rounded-xl border border-line bg-surface-900 px-6 py-12 text-center">
              <h2 className="text-lg font-semibold text-white">Vote packages are unavailable</h2>
              <p className="mt-2 text-sm text-gray-400">No packages are available at the moment. Please try again later.</p>
            </div>
          ) : (
            <>
              {/* Daily Packages Section */}
              {daily.length > 0 && (
                <div className="mb-12">
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <div className="w-1 h-6 bg-accent rounded mr-3"></div>
                    <h2 className="section-title">Daily Packages</h2>
                    <span className="text-sm text-gray-500">Instant votes, perfect for quick boosts</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {daily.map((pkg) => (
                      <VotePackageCard
                        key={pkg.packageId}
                        package={pkg}
                        onSelect={handleSelectPackage}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Weekly Packages Section */}
              {weekly.length > 0 && (
                <div className="mb-12">
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <div className="w-1 h-6 bg-accent rounded mr-3"></div>
                    <h2 className="section-title">
                      Weekly Packages
                    </h2>
                    <span className="text-sm text-gray-500">Best value: more votes over time</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {weekly.map((pkg) => (
                      <VotePackageCard
                        key={pkg.packageId}
                        package={pkg}
                        onSelect={handleSelectPackage}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* FAQ Section */}
          <section className="mt-12 rounded-xl border border-line bg-surface-900 p-5 sm:p-8">
            <h3 className="section-title mb-8">
              Frequently Asked Questions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-base font-semibold text-white mb-2">
                  How are votes used?
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  You can use your votes in any voting contest or poll on the platform. Each vote counts as one unit of support.
                </p>
              </div>
              <div>
                <h4 className="text-base font-semibold text-white mb-2">
                  Do unused votes expire?
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Daily votes expire after 24 hours if unused. Weekly votes expire after 7 days. Always use them within the validity period!
                </p>
              </div>
              <div>
                <h4 className="text-base font-semibold text-white mb-2">
                  What payment methods are accepted?
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We accept payments via MTN Mobile Money, Airtel Money, and Zamtel. Simple and fast!
                </p>
              </div>
              <div>
                <h4 className="text-base font-semibold text-white mb-2">
                  Is my payment secure?
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Yes! We use industry-standard encryption and Lenco's trusted payment gateway for all transactions.
                </p>
              </div>
            </div>
          </section>

          {/* Info Banner */}
          <div className="mt-6 rounded-xl border border-line bg-surface-900 p-5 text-center">
            <p className="text-gray-400 text-sm">
              💡 <span className="font-semibold text-white">Pro Tip:</span> Weekly packages offer
              better value than daily ones. Stack them for maximum voting power!
            </p>
          </div>
      </div>

      {/* Purchase Modal */}
      <VotePurchaseModal
        isOpen={showModal}
        package={selectedPackage}
        onClose={() => {
          setShowModal(false);
          setSelectedPackage(null);
        }}
        onSuccess={handlePurchaseSuccess}
      />
    </VotingPageShell>
  );
}

export default BuyVotes;
